import { useEffect, useRef } from "react";
import { wsURL } from "../lib/api";

/**
 * Realtime order socket with self-healing:
 * - restaurant/user scoped subscription (server enforces isolation)
 * - heartbeat ping/pong -> detects dead (half-open) sockets and forces reconnect
 * - auto-reconnect with a recovery fetch (opts.onReady) after any drop
 * - visibilitychange/online recovery (background tab, PWA, network switch)
 *
 * @param onMessage (msg) => void
 * @param opts { restaurantId?, userId?, onReady? }
 */
export function useOrderSocket(onMessage, opts = {}) {
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const subscribeRef = useRef(() => {});

  useEffect(() => {
    let ws;
    let closed = false;
    let retry;
    let hb;
    let pongTimer;
    let everConnected = false;

    const send = (obj) => {
      try { if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj)); } catch {}
    };
    const subscribe = () => {
      const { restaurantId, userId } = optsRef.current || {};
      send({ action: "subscribe", restaurant_ids: restaurantId ? [restaurantId] : [], user_id: userId || null });
    };
    subscribeRef.current = subscribe;
    const recover = () => { try { optsRef.current?.onReady && optsRef.current.onReady(); } catch {} };

    const startHeartbeat = () => {
      clearInterval(hb);
      hb = setInterval(() => {
        send({ action: "ping" });
        clearTimeout(pongTimer);
        pongTimer = setTimeout(() => { try { ws && ws.close(); } catch {} }, 10000);
      }, 25000);
    };

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(wsURL());
        ws.onopen = () => {
          console.log("[orders-ws] connected", optsRef.current?.restaurantId || "");
          subscribe();
          startHeartbeat();
          if (everConnected) recover(); // reconnect -> reconcile missed events
          everConnected = true;
        };
        ws.onmessage = (e) => {
          let msg;
          try { msg = JSON.parse(e.data); } catch { return; }
          if (msg.type === "pong") { clearTimeout(pongTimer); return; }
          if (msg.type === "ping") { clearTimeout(pongTimer); send({ action: "ping" }); return; }
          cbRef.current(msg);
        };
        ws.onclose = () => {
          clearInterval(hb);
          clearTimeout(pongTimer);
          if (!closed) { console.log("[orders-ws] closed, reconnecting"); retry = setTimeout(connect, 2000); }
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        retry = setTimeout(connect, 2000);
      }
    };
    connect();

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!ws || ws.readyState > 1) connect();
      else { subscribe(); recover(); }
    };
    const onOnline = () => { if (!ws || ws.readyState > 1) connect(); else recover(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      closed = true;
      clearTimeout(retry);
      clearInterval(hb);
      clearTimeout(pongTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      if (ws) try { ws.close(); } catch {}
    };
  }, []);

  // Re-subscribe if the scope (restaurant/user) changes without a remount.
  useEffect(() => { subscribeRef.current && subscribeRef.current(); }, [opts.restaurantId, opts.userId]);
}
